import React, { useCallback, useEffect, useState } from "react";
import PageTitle from "../PageTitle";
import useAxios from "../../utilities/useAxios";
import {
	Button,
	Card,
	Form,
	Input,
	Modal,
	Select,
	Space,
	Switch,
	Table,
	Typography,
	message,
} from "antd";
import { DeleteOutlined, PlusOutlined } from "@ant-design/icons";
import PageHeader from "../common/PageHeader";
import useIsMobile from "../../hooks/useIsMobile";

const { Text } = Typography;

/** 결재자 Select에서 팀 리더(동적)를 가리키는 특수 값 */
const TEAM_LEADER_VALUE = "team_leader";

const ApprovalRouteManage = () => {
	const isMobile = useIsMobile();
	const [loading, setLoading] = useState(false);
	const [saving, setSaving] = useState(false);
	const [routes, setRoutes] = useState([]);
	const [users, setUsers] = useState([]);
	const [selectedRouteId, setSelectedRouteId] = useState(null);
	const [steps, setSteps] = useState([]);
	const [createOpen, setCreateOpen] = useState(false);
	const [renameOpen, setRenameOpen] = useState(false);
	const [createForm] = Form.useForm();
	const [renameForm] = Form.useForm();

	const selectedRoute = routes.find((r) => r.id === selectedRouteId) || null;

	const loadRoutes = useCallback(async () => {
		try {
			setLoading(true);
			const res = await useAxios.get("/utils/leave/approval-routes/");
			const list = res.data || [];
			setRoutes(list);
			setSelectedRouteId((prev) => {
				if (prev && list.some((r) => r.id === prev)) return prev;
				return list[0]?.id ?? null;
			});
		} catch {
			message.error("결재 순서를 불러오지 못했습니다.");
		} finally {
			setLoading(false);
		}
	}, []);

	const loadUsers = useCallback(async () => {
		try {
			const res = await useAxios.get("/users/clergy-staff/");
			setUsers(res.data || []);
		} catch {
			setUsers([]);
		}
	}, []);

	useEffect(() => {
		loadRoutes();
		loadUsers();
	}, [loadRoutes, loadUsers]);

	useEffect(() => {
		if (!selectedRoute) {
			setSteps([]);
			return;
		}
		setSteps(
			(selectedRoute.steps || []).map((s) => ({
				key: `step-${s.id}`,
				approver_kind: s.approver_kind || "user",
				approver_id:
					s.approver_kind === TEAM_LEADER_VALUE
						? TEAM_LEADER_VALUE
						: s.approver_id,
				send_push: Boolean(s.send_push),
			})),
		);
	}, [selectedRoute]);

	const createRoute = async () => {
		const values = await createForm.validateFields();
		try {
			setSaving(true);
			const res = await useAxios.post("/utils/leave/approval-routes/", {
				name: values.name,
			});
			message.success("결재 순서를 추가했습니다.");
			setCreateOpen(false);
			createForm.resetFields();
			await loadRoutes();
			setSelectedRouteId(res.data.id);
		} catch (error) {
			message.error(
				error?.response?.data?.error || "추가에 실패했습니다.",
			);
		} finally {
			setSaving(false);
		}
	};

	const renameRoute = async () => {
		const values = await renameForm.validateFields();
		try {
			setSaving(true);
			await useAxios.put(
				`/utils/leave/approval-routes/${selectedRouteId}/`,
				{ name: values.name },
			);
			message.success("이름을 변경했습니다.");
			setRenameOpen(false);
			await loadRoutes();
		} catch (error) {
			message.error(
				error?.response?.data?.error || "이름 변경에 실패했습니다.",
			);
		} finally {
			setSaving(false);
		}
	};

	const deleteRoute = () => {
		Modal.confirm({
			title: "결재 순서를 삭제할까요?",
			content: selectedRoute?.name,
			okText: "삭제",
			okButtonProps: { danger: true },
			onOk: async () => {
				try {
					await useAxios.delete(
						`/utils/leave/approval-routes/${selectedRouteId}/`,
					);
					message.success("삭제했습니다.");
					await loadRoutes();
				} catch (error) {
					message.error(
						error?.response?.data?.error ||
							"삭제에 실패했습니다.",
					);
				}
			},
		});
	};

	const saveSteps = async () => {
		if (!selectedRouteId) return;
		const payload = steps.map((row, index) => {
			const isTeamLeader =
				row.approver_id === TEAM_LEADER_VALUE ||
				row.approver_kind === TEAM_LEADER_VALUE;
			return {
				order: index + 1,
				approver_kind: isTeamLeader ? TEAM_LEADER_VALUE : "user",
				approver_id: isTeamLeader ? null : row.approver_id,
				send_push: Boolean(row.send_push),
			};
		});
		if (
			payload.some(
				(s) =>
					s.approver_kind === "user" && !s.approver_id,
			)
		) {
			message.warning("모든 단계에 결재자를 지정하세요.");
			return;
		}
		try {
			setSaving(true);
			const res = await useAxios.put(
				`/utils/leave/approval-routes/${selectedRouteId}/steps/`,
				{ steps: payload },
			);
			message.success("결재 단계를 저장했습니다.");
			setRoutes((prev) =>
				prev.map((r) => (r.id === res.data.id ? res.data : r)),
			);
		} catch (error) {
			message.error(
				error?.response?.data?.error || "저장에 실패했습니다.",
			);
		} finally {
			setSaving(false);
		}
	};

	const stepColumns = [
		{
			title: "순서",
			width: 70,
			render: (_, __, index) => index + 1,
		},
		{
			title: "결재자",
			dataIndex: "approver_id",
			render: (value, record, index) => (
				<Select
					showSearch
					style={{ width: "100%", minWidth: isMobile ? 0 : 220 }}
					placeholder="결재자 선택"
					value={value}
					optionFilterProp="label"
					options={[
						{
							value: TEAM_LEADER_VALUE,
							label: "팀 리더 (신청자 소속 팀)",
						},
						...users.map((u) => ({
							value: u.id,
							label: `${u.name} (${u.classification_name || "-"})`,
						})),
					]}
					onChange={(next) => {
						setSteps((prev) =>
							prev.map((row, i) =>
								i === index
									? {
											...row,
											approver_id: next,
											approver_kind:
												next ===
												TEAM_LEADER_VALUE
													? TEAM_LEADER_VALUE
													: "user",
										}
									: row,
							),
						);
					}}
				/>
			),
		},
		{
			title: "앱 푸시",
			dataIndex: "send_push",
			width: 100,
			align: "center",
			render: (checked, _, index) => (
				<Switch
					checked={checked}
					onChange={(next) => {
						setSteps((prev) =>
							prev.map((row, i) =>
								i === index
									? { ...row, send_push: next }
									: row,
							),
						);
					}}
				/>
			),
		},
		{
			title: "",
			width: 60,
			render: (_, __, index) => (
				<Button
					type="text"
					danger
					icon={<DeleteOutlined />}
					onClick={() =>
						setSteps((prev) => prev.filter((_, i) => i !== index))
					}
				/>
			),
		},
	];

	return (
		<>
			<PageTitle title="결재 순서 관리" />
			<div className="page">
				<PageHeader
					title="결재 순서 관리"
				/>
				<Card className="surface-card" loading={loading}>
					<Space style={{ marginBottom: 16 }} wrap>
						<Select
							style={{
								minWidth: isMobile ? 0 : 220,
								width: isMobile ? "100%" : undefined,
							}}
							value={selectedRouteId}
							onChange={setSelectedRouteId}
							options={routes.map((r) => ({
								value: r.id,
								label: r.name,
							}))}
						/>
						<Button
							icon={<PlusOutlined />}
							onClick={() => setCreateOpen(true)}>
							종류 추가
						</Button>
						<Button
							disabled={!selectedRoute}
							onClick={() => {
								renameForm.setFieldsValue({
									name: selectedRoute?.name,
								});
								setRenameOpen(true);
							}}>
							이름 변경
						</Button>
						<Button
							danger
							disabled={!selectedRoute}
							onClick={deleteRoute}>
							삭제
						</Button>
					</Space>

					{selectedRoute && (
						<>
							<Text type="secondary" style={{ display: "block", marginBottom: 12 }}>
								단계마다 결재자와 앱 푸시 여부를 지정합니다. 「팀 리더」는 특정 사람이 아니라
								신청자가 속한 팀의 리더를 가리킵니다. 새 단계의 앱 푸시는 기본적으로 켜져 있습니다.
							</Text>
							<Table
								rowKey="key"
								pagination={false}
								columns={stepColumns}
								dataSource={steps}
								locale={{ emptyText: "단계가 없습니다. 추가하세요." }}
								sticky={{ offsetHeader: 64 }}
							/>
							<Space style={{ marginTop: 16 }}>
								<Button
									icon={<PlusOutlined />}
									onClick={() =>
										setSteps((prev) => [
											...prev,
											{
												key: `new-${Date.now()}`,
												approver_kind: "user",
												approver_id: undefined,
												send_push: true,
											},
										])
									}>
									단계 추가
								</Button>
								<Button
									type="primary"
									loading={saving}
									onClick={saveSteps}>
									단계 저장
								</Button>
							</Space>
						</>
					)}
				</Card>

				<Modal
					title="결재 순서 추가"
					open={createOpen}
					onCancel={() => setCreateOpen(false)}
					onOk={createRoute}
					confirmLoading={saving}
					destroyOnClose>
					<Form form={createForm} layout="vertical">
						<Form.Item
							name="name"
							label="이름"
							rules={[{ required: true, message: "이름을 입력하세요" }]}>
							<Input placeholder="예: 일반 교직원" />
						</Form.Item>
					</Form>
				</Modal>

				<Modal
					title="이름 변경"
					open={renameOpen}
					onCancel={() => setRenameOpen(false)}
					onOk={renameRoute}
					confirmLoading={saving}
					destroyOnClose>
					<Form form={renameForm} layout="vertical">
						<Form.Item
							name="name"
							label="이름"
							rules={[{ required: true, message: "이름을 입력하세요" }]}>
							<Input />
						</Form.Item>
					</Form>
				</Modal>
			</div>
		</>
	);
};

export default ApprovalRouteManage;
